import { faClock, faCheckCircle, faTag } from '@fortawesome/free-solid-svg-icons';

/**
 * Helper para obtener estilos e icono según el estado de la incidencia
 */
export const getStatusBadge = (status, isDark) => {
  switch (Number(status)) {
    case 2:
      return {
        label: 'En Progreso',
        icon: faClock,
        bg: isDark ? 'rgba(245, 158, 11, 0.15)' : '#fef3c7',
        color: isDark ? '#fbbf24' : '#b45309',
        border: isDark ? 'rgba(245, 158, 11, 0.3)' : '#fde68a'
      };
    case 3:
      return {
        label: 'Resuelta',
        icon: faCheckCircle,
        bg: isDark ? 'rgba(16, 185, 129, 0.15)' : '#d1fae5',
        color: isDark ? '#34d399' : '#047857',
        border: isDark ? 'rgba(16, 185, 129, 0.3)' : '#a7f3d0'
      };
    default:
      return {
        label: 'Abierta',
        icon: faTag,
        bg: isDark ? 'rgba(59, 130, 246, 0.15)' : '#dbeafe',
        color: isDark ? '#60a5fa' : '#1d4ed8',
        border: isDark ? 'rgba(59, 130, 246, 0.3)' : '#bfdbfe'
      };
  }
};

/**
 * Helper para obtener estilos del estado del ticket (1: Abierto, 2: Cerrado, 3: En progreso)
 */
export const getTicketStatusBadge = (status, isDark) => {
  switch (Number(status)) {
    case 2:
      return {
        label: 'Cerrado',
        bg: isDark ? 'rgba(16, 185, 129, 0.15)' : '#d1fae5',
        color: isDark ? '#34d399' : '#047857',
        border: isDark ? 'rgba(16, 185, 129, 0.3)' : '#a7f3d0'
      };
    case 3:
      return {
        label: 'En Progreso',
        bg: isDark ? 'rgba(245, 158, 11, 0.15)' : '#fef3c7',
        color: isDark ? '#fbbf24' : '#b45309',
        border: isDark ? 'rgba(245, 158, 11, 0.3)' : '#fde68a'
      };
    default:
      return {
        label: 'Abierto',
        bg: isDark ? 'rgba(59, 130, 246, 0.15)' : '#dbeafe',
        color: isDark ? '#60a5fa' : '#1d4ed8',
        border: isDark ? 'rgba(59, 130, 246, 0.3)' : '#bfdbfe'
      };
  }
};

/**
 * Formatea el tiempo transcurrido desde la creación (ej. "hace 2h", "hace 1 día, 4h", etc.)
 */
export const formatTimeAgo = dateStr => {
  if (!dateStr) return 'hace un momento';
  const created = new Date(dateStr);
  if (isNaN(created.getTime())) return 'hace un momento';

  const diffMs = Math.max(0, Date.now() - created.getTime());
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 1) return 'hace un momento';
  if (diffMinutes < 60) return `hace ${diffMinutes}m`;
  if (diffHours < 24) return `hace ${diffHours}h`;
  const remainingHours = diffHours % 24;
  if (remainingHours === 0) return `hace ${diffDays}d`;
  return `hace ${diffDays}d, ${remainingHours}h`;
};

/**
 * Formatea minutos a formato legible (ej. "< 1m", "45m", "3h 20m", "2d 4h")
 */
export const formatDurationMinutes = totalMinutes => {
  if (totalMinutes === null || totalMinutes === undefined || isNaN(totalMinutes) || totalMinutes < 0) {
    return null;
  }
  const mins = Math.floor(totalMinutes);
  if (mins < 1) return '< 1m';
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    const remHours = hours % 24;
    return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`;
  }
  if (hours > 0) {
    const remMins = mins % 60;
    return remMins > 0 ? `${hours}h ${remMins}m` : `${hours}h`;
  }
  return `${mins}m`;
};

/**
 * Formatea una duración en milisegundos a formato legible (ej. "< 1m", "45m", "3h 20m", "2d 4h")
 */
export const formatDurationMs = ms => {
  if (ms === null || ms === undefined || isNaN(ms) || ms < 0) return null;
  const totalMinutes = Math.floor(ms / (1000 * 60));
  return formatDurationMinutes(totalMinutes);
};

/**
 * Obtiene la información del tiempo y duración de una incidencia.
 * - Si está resuelta (iEstadoIncidencia === 3):
 *   El tiempo NO sigue corriendo; visualiza el tiempo exacto que tomó resolverla
 *   (desde que entró en progreso/creación hasta su resolución).
 *   Ejemplo: "Resuelta en 4h 15m", "Resuelta en 2d 5h", "Resuelta en < 1m".
 * - Si sigue abierta o en progreso:
 *   Sigue contando el tiempo activo desde la creación hasta el momento actual ("Creada hace Xh").
 */
export const getIncidentTimeInfo = (createdDateOrTag, updatedDate, status) => {
  let createdDate = createdDateOrTag;
  let updDate = updatedDate;
  let inProgressDate = null;
  let st = status;
  let resolutionMinutes = null;

  if (createdDateOrTag && typeof createdDateOrTag === 'object' && !(createdDateOrTag instanceof Date)) {
    createdDate = createdDateOrTag.dCreatedDate || createdDateOrTag.createdDate || createdDateOrTag.dFechaCreacion;
    updDate = createdDateOrTag.dFechaResolucion || createdDateOrTag.dUpdatedDate || createdDateOrTag.updatedDate || createdDateOrTag.dFechaCierre || createdDateOrTag.dFechaActualizacion;
    inProgressDate = createdDateOrTag.dFechaProgreso || createdDateOrTag.inProgressDate;
    st = createdDateOrTag.iEstadoIncidencia;
    if (createdDateOrTag.iTiempoResolucionMinutos !== undefined && createdDateOrTag.iTiempoResolucionMinutos !== null) {
      resolutionMinutes = Number(createdDateOrTag.iTiempoResolucionMinutos);
    }
  }

  const isResolved = Number(st) === 3;

  if (isResolved) {
    // 1. Si la BD ya trae iTiempoResolucionMinutos
    if (resolutionMinutes !== null && !isNaN(resolutionMinutes) && resolutionMinutes >= 0) {
      const durationStr = formatDurationMinutes(resolutionMinutes) || '< 1m';
      return {
        label: `Resuelta en ${durationStr}`,
        text: `Resuelta en ${durationStr}`,
        duration: durationStr,
        isResolved: true
      };
    }

    // 2. Si hay fechas en BD (resolución - progreso o creación)
    const startDate = inProgressDate || createdDate;
    const start = startDate ? new Date(startDate).getTime() : null;
    const end = updDate ? new Date(updDate).getTime() : null;

    if (start && end && !isNaN(start) && !isNaN(end) && end >= start) {
      const diffMs = Math.max(0, end - start);
      const durationStr = formatDurationMs(diffMs) || '< 1m';
      return {
        label: `Resuelta en ${durationStr}`,
        text: `Resuelta en ${durationStr}`,
        duration: durationStr,
        isResolved: true
      };
    }

    // 3. Fallback para registros resueltos donde la fecha de resolución no se guardó previamente
    return {
      label: 'Resuelta en < 1m',
      text: 'Resuelta en < 1m',
      duration: '< 1m',
      isResolved: true
    };
  }

  if (Number(st) === 2) {
    const timeAgo = formatTimeAgo(inProgressDate || createdDate);
    return {
      label: `En progreso ${timeAgo}`,
      text: `En progreso ${timeAgo}`,
      duration: timeAgo,
      isResolved: false
    };
  }

  const timeAgo = formatTimeAgo(createdDate);
  return {
    label: `Creada ${timeAgo}`,
    text: `Creada ${timeAgo}`,
    duration: timeAgo,
    isResolved: false
  };
};

/**
 * Formatea tamaño de archivos en bytes a formato legible (KB, MB)
 */
export const formatFileSize = bytes => {
  if (!bytes || isNaN(bytes)) return '0 KB';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Obtiene extensión y categoría de un archivo
 */
export const getFileMeta = fileName => {
  if (!fileName) return { ext: 'FILE', type: 'other', label: 'Archivo' };
  const parts = fileName.split('.');
  const ext = parts.length > 1 ? parts.pop().toUpperCase() : 'FILE';
  const lowerExt = ext.toLowerCase();

  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(lowerExt)) {
    return { ext, type: 'image', label: ext };
  }
  if (['txt', 'log'].includes(lowerExt)) {
    return { ext, type: 'log', label: ext };
  }
  if (['json', 'js', 'ts', 'html', 'css', 'sql'].includes(lowerExt)) {
    return { ext, type: 'code', label: ext };
  }
  if (['pdf'].includes(lowerExt)) {
    return { ext, type: 'pdf', label: 'PDF' };
  }
  return { ext, type: 'other', label: ext };
};

